import { useEffect, useMemo, useState } from 'react';
import { History } from 'lucide-react';
import { Badge, Button, Skeleton } from '@vnetwork/ui/primitives';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState, Timeline } from '@vnetwork/ui/data';
import { useCustomerActivityLogs, type ActivityLogRow } from '@/api/customer-activity';
import { activityLogToTimelineItem } from './activity-log-item';

/**
 * Customer activity history — the "Lịch sử hoạt động" timeline for the customer
 * detail. Fetches paged activity logs, groups them by LOCAL calendar date, and
 * renders each day as a header (weekday · DD Mon YYYY + count) over a vertical
 * timeline. "Load more" appends the next page. States: loading / error / empty.
 */

const PAGE_SIZE = 20;

/** Local `YYYY-MM-DD` key — NOT toISOString (that shifts to UTC in UTC+7). */
function localDateKey(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO → "Tue · 01 Jul 2026" group header label. */
function groupDateLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${WEEKDAYS[d.getDay()]} · ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

interface DateGroup {
  key: string;
  label: string;
  rows: ActivityLogRow[];
}

/** Group rows by local date, preserving the (newest-first) order the API returns. */
function groupByDate(rows: ActivityLogRow[]): DateGroup[] {
  const groups: DateGroup[] = [];
  const index = new Map<string, DateGroup>();
  for (const row of rows) {
    const key = localDateKey(row.created_at);
    let group = index.get(key);
    if (!group) {
      group = { key, label: groupDateLabel(row.created_at), rows: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

function TimelineSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-3)' }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'flex-start' }}>
          <Skeleton variant="circle" width={24} height={24} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)' }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="70%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CustomerActivityTimeline({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<ActivityLogRow[]>([]);

  const query = useCustomerActivityLogs(customerId, { page, page_size: PAGE_SIZE });
  const pageRows = query.data?.results;
  const pagination = query.data?.pagination;

  // Append each fetched page into the running list, de-duped by log_id. Keyed on
  // the query's own data object so it commits exactly once per successful fetch.
  useEffect(() => {
    if (!pageRows) return;
    setAccumulated((prev) => {
      const seen = new Set(prev.map((r) => r.log_id));
      const next = pageRows.filter((r) => !seen.has(r.log_id));
      return next.length ? [...prev, ...next] : prev;
    });
  }, [pageRows]);

  const rows = accumulated;
  const groups = useMemo(() => groupByDate(rows), [rows]);
  const totalPages = pagination?.total_pages ?? 1;
  const hasMore = page < totalPages;
  const total = pagination?.total ?? rows.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity history</CardTitle>
        <CardDescription>Recent actions on this customer{total ? ` · ${total} total` : ''}</CardDescription>
      </CardHeader>
      <CardBody>
        {query.isError ? (
          <Alert
            tone="danger"
            title="Couldn't load activity history"
            actions={
              <Button variant="secondary" size="sm" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            {(query.error as Error)?.message ?? 'Please try again.'}
          </Alert>
        ) : query.isLoading && rows.length === 0 ? (
          <TimelineSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState icon={<History size={28} />} title="No activity yet" description="Actions on this customer will appear here." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-5)' }}>
            {groups.map((group) => (
              <section key={group.key}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-2)',
                    marginBottom: 'var(--vn-space-3)',
                  }}
                >
                  <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>
                    {group.label}
                  </span>
                  <Badge tone="neutral" appearance="subtle" size="sm">
                    {group.rows.length}
                  </Badge>
                </div>
                <Timeline items={group.rows.map(activityLogToTimelineItem)} size="sm" />
              </section>
            ))}

            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={query.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default CustomerActivityTimeline;
